/**
 * Simulated Hitz economy for the mock platform and the stress test. Pure TypeScript, no game imports: it runs in the host page (dev only) and in Node.
 * It follows "Quem paga o prêmio" (docs/artifacts/quem-paga-o-premio.html) and the Prado specs; every number comes from params.json (see PARAMS.md
 * for the source of each). None of this exists in the game: the iframe only speaks the wire v1 and never sees balance, bet or prize (AGENTS.md).
 * Where the document defines something the scaffold does not have yet (bet modal, piggy top-up, progression pot, event settlement, bar per level),
 * this is where that definition is tried out (docs/PLATFORM_SIM.md has the concept → where it would live → status table).
 */

export interface LevelParams { phaseFactor: number; medianRef: number }
export interface Params {
  wallet: { start: number; bets: number[] }
  loss: { piggyShare: number; houseTopUp: number; generalShare: number }
  general: { progression: number; hitz: number; ranking: number }
  finite: { coefficient: number; exponent: number; cap: number; bonusMode: 'fixed' | 'median-scaled'; minWinsForMedian: number; levels: Record<string, LevelParams> }
  bar: { uMin: number; pMax: number; floorFactor: number | null; maxDailyDropPct: number | null; pIni: Record<string, number>; pIniDefault: number }
  ranking: { scale: number; shares: number[]; capBRL: number }
  settlement: 'event' | 'immediate'
}

/** The bar of one level (BAR §4): p in percentage points, users of the window, and what was observed so far. */
export interface LevelState { id: string; pIni: number; p: number; pDayStart: number; users24h: number; played: number; won: number; winScores: number[] }
export interface PendingBonus { attemptId: string; levelId: string; bet: number; score: number; requested: number; pAtBet: number }
export interface RankingEntry { name: string; points: number; score: number; example?: boolean }
export interface Totals { matches: number; bets: number; lost: number; wins: number; bonusRequested: number; bonusPaid: number; events: number; limitedEvents: number; piggyTopUp: number }
export interface State {
  wallet: number
  piggy: number
  pots: { progression: number; hitz: number; ranking: number }
  levels: Record<string, LevelState>
  day: number
  event: { requested: PendingBonus[]; inflow: number; bets: number }
  openBets: Record<string, { bet: number; levelId: string; pAtBet: number }>
  ranking: RankingEntry[]
  totals: Totals
  history: EventClose[]
}
export interface LossSplit { piggyFromBet: number; piggyTopUp: number; piggyShown: number; general: number; progression: number; hitz: number; ranking: number }
export interface Settlement {
  kind: 'win' | 'loss'
  attemptId: string
  levelId: string
  bet: number
  score: number
  /** the principal back to the balance on a win (QPP §2: "100% da aposta volta") */
  returned: number
  /** bonus asked from the progression pot (paid at the end of the event, or at once when settlement = immediate) */
  bonusRequested: number
  bonusPct: number
  /** paid right away (immediate mode) or 0 while it waits for the event to close */
  bonusPaidNow: number
  split: LossSplit | null
  pBefore: number
  pAfter: number
  rankingPoints: number
}
export interface EventClose { day: number; requested: number; available: number; solvency: number; paid: number; limited: boolean; count: number }
export interface RankingPrize { name: string; position: number; prize: number; example: boolean }

export const round2 = (v: number) => Math.round(v * 100) / 100

export function createState(params: Params): State {
  return {
    wallet: params.wallet.start, piggy: 0, pots: { progression: 0, hitz: 0, ranking: 0 }, levels: {}, day: 1,
    event: { requested: [], inflow: 0, bets: 0 }, openBets: {}, ranking: [],
    totals: { matches: 0, bets: 0, lost: 0, wins: 0, bonusRequested: 0, bonusPaid: 0, events: 0, limitedEvents: 0, piggyTopUp: 0 }, history: []
  }
}

export function levelOf(state: State, levelId: string, params: Params): LevelState {
  let l = state.levels[levelId]
  if (!l) {
    const pIni = params.bar.pIni[levelId] ?? params.bar.pIniDefault
    l = state.levels[levelId] = { id: levelId, pIni, p: pIni, pDayStart: pIni, users24h: 0, played: 0, won: 0, winScores: [] }
  }
  return l
}

/** Where a lost bet goes (BAR §7, QPP §2): 80% to the piggy plus the house's 20% top-up so it shows 100%; 20% to the general pot, split 89,11 / 10 / 0,89. */
export function splitLoss(bet: number, params: Params): LossSplit {
  const piggyFromBet = bet * params.loss.piggyShare, piggyTopUp = bet * params.loss.houseTopUp, general = bet * params.loss.generalShare
  return { piggyFromBet, piggyTopUp, piggyShown: piggyFromBet + piggyTopUp, general, progression: general * params.general.progression, hitz: general * params.general.hitz, ranking: general * params.general.ranking }
}

/** Share of a lost bet that reaches the progression pot: the K of the equilibrium formula (KEE: 0,8911 × 0,20 = 0,17822). */
export const progressionShare = (params: Params) => params.loss.generalShare * params.general.progression

export const median = (xs: readonly number[]) => { if (!xs.length) return 0; const s = [...xs].sort((a, b) => a - b); const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2 }

/** Finite family (KEE, PAC): %bonus = min(coefficient × phaseFactor × score^exponent, cap). In `median-scaled` mode the score is rescaled by medianRef / observed median (QPP §11, round 1: open question). */
export function finiteBonusPct(score: number, levelId: string, params: Params, level?: LevelState): number {
  const lp = params.finite.levels[levelId] ?? { phaseFactor: 1, medianRef: 0 }
  let s = Math.max(0, score)
  if (params.finite.bonusMode === 'median-scaled' && level && lp.medianRef > 0 && level.winScores.length >= params.finite.minWinsForMedian) {
    const obs = median(level.winScores)
    if (obs > 0) s = (s * lp.medianRef) / obs
  }
  return Math.min(params.finite.coefficient * lp.phaseFactor * Math.pow(s, params.finite.exponent), params.finite.cap)
}

/** Win rate at which a level pays exactly what it collects (KEE): p* = K / (K + meanBonusPct). Above it, the pot needs other levels or the rateio. */
export const equilibriumWinRate = (meanBonusPct: number, params: Params) => { const k = progressionShare(params); return k / (k + meanBonusPct) }

/** BAR §4: one step of the bar after a result. `floorFactor` is the spec's floor (0,9 × pIni); `maxDailyDropPct` is the "10% per day" of the conversation (QPP §10). Both optional. */
export function updateBar(level: LevelState, won: boolean, params: Params): number {
  const eta = 1 / Math.max(level.users24h, params.bar.uMin)
  let p = won ? level.p + eta * (100 - level.p) : level.p - eta * level.p
  if (params.bar.floorFactor != null) p = Math.max(p, params.bar.floorFactor * level.pIni)
  if (params.bar.maxDailyDropPct != null) p = Math.max(p, level.pDayStart * (1 - params.bar.maxDailyDropPct / 100))
  p = Math.min(p, params.bar.pMax)
  level.p = p
  return p
}

/** Ranking points of a won match (Prado, ranking spec): score × √(scale / p), p = the level's bar at the time of the bet. */
export const rankingPoints = (score: number, pAtBet: number, params: Params) => (pAtBet > 0 ? score * Math.sqrt(params.ranking.scale / pAtBet) : 0)

/** The platform debits the bet and records the p of the bet (BAR §6: the bonus uses the p that was current when the bet was placed). */
export function placeBet(state: State, params: Params, attemptId: string, levelId: string, bet: number): { pAtBet: number } {
  if (bet < 0 || bet > state.wallet + 1e-9) throw new Error(`insufficient balance for a bet of ${bet}`)
  const level = levelOf(state, levelId, params)
  state.wallet -= bet
  state.openBets[attemptId] = { bet, levelId, pAtBet: level.p }
  level.users24h++ // one visit per bet: the mock has one player, the stress test counts matches as users
  state.event.bets += bet
  state.totals.bets += bet
  return { pAtBet: level.p }
}

/** Result of an attempt. A win returns the principal and asks the bonus from the pot; anything else (loss, abandon, quit) is a lost bet. */
export function resolveAttempt(state: State, params: Params, attemptId: string, outcome: { won: boolean; score: number }): Settlement {
  const open = state.openBets[attemptId]
  if (!open) throw new Error(`no open bet for ${attemptId}`)
  delete state.openBets[attemptId]
  const level = levelOf(state, open.levelId, params)
  const pBefore = level.p
  level.played++
  state.totals.matches++
  let s: Settlement
  if (outcome.won) {
    level.won++
    level.winScores.push(outcome.score); if (level.winScores.length > 500) level.winScores.shift()
    const pct = finiteBonusPct(outcome.score, open.levelId, params, level)
    const requested = open.bet * pct
    state.wallet += open.bet
    state.totals.wins++
    state.totals.bonusRequested += requested
    const points = rankingPoints(outcome.score, open.pAtBet, params)
    s = { kind: 'win', attemptId, levelId: open.levelId, bet: open.bet, score: outcome.score, returned: open.bet, bonusRequested: requested, bonusPct: pct, bonusPaidNow: 0, split: null, pBefore, pAfter: pBefore, rankingPoints: points }
    if (params.settlement === 'immediate') {
      const paid = Math.min(requested, state.pots.progression)
      state.pots.progression -= paid; state.wallet += paid; state.totals.bonusPaid += paid; s.bonusPaidNow = paid
    } else state.event.requested.push({ attemptId, levelId: open.levelId, bet: open.bet, score: outcome.score, requested, pAtBet: open.pAtBet })
    addRanking(state, 'Você', points, outcome.score)
  } else {
    const split = splitLoss(open.bet, params)
    state.piggy += split.piggyShown
    state.pots.progression += split.progression; state.pots.hitz += split.hitz; state.pots.ranking += split.ranking
    state.event.inflow += split.progression
    state.totals.lost += open.bet; state.totals.piggyTopUp += split.piggyTopUp
    s = { kind: 'loss', attemptId, levelId: open.levelId, bet: open.bet, score: outcome.score, returned: 0, bonusRequested: 0, bonusPct: 0, bonusPaidNow: 0, split, pBefore, pAfter: pBefore, rankingPoints: 0 }
  }
  s.pAfter = updateBar(level, outcome.won, params)
  return s
}

function addRanking(state: State, name: string, points: number, score: number) {
  const e = state.ranking.find((r) => r.name === name && !r.example)
  if (e) { e.points += points; e.score = Math.max(e.score, score) } else state.ranking.push({ name, points, score })
}

/** End of the event (BAR §7): solvency = min(1, available / requested); everyone is paid the same share; a share under 1 is a "limited" event, the main risk indicator. */
export function closeEvent(state: State, params: Params): EventClose {
  const requested = state.event.requested.reduce((a, b) => a + b.requested, 0)
  const available = state.pots.progression
  const solvency = requested > 0 ? Math.min(1, available / requested) : 1
  const paid = requested * solvency
  state.pots.progression = available - paid
  state.wallet += paid
  state.totals.bonusPaid += paid
  state.totals.events++
  const close: EventClose = { day: state.day, requested, available, solvency, paid, limited: solvency < 1 - 1e-9, count: state.event.requested.length }
  if (close.limited) state.totals.limitedEvents++
  state.history.push(close); if (state.history.length > 400) state.history.shift()
  state.event = { requested: [], inflow: 0, bets: 0 }
  void params
  return close
}

/** Prizes of a ranking period (Prado): top 3 take 50 / 15 / 5% of the pool under a cap; ties share the sum of the positions they occupy; example rows are not paid. */
export function rankingPrizes(entries: readonly RankingEntry[], pool: number, params: Params): RankingPrize[] {
  const sorted = [...entries].sort((a, b) => b.points - a.points)
  const total = Math.min(pool, params.ranking.capBRL)
  const out: RankingPrize[] = []
  let i = 0
  while (i < sorted.length) {
    let j = i
    while (j + 1 < sorted.length && sorted[j + 1].points === sorted[i].points) j++
    const share = params.ranking.shares.slice(i, j + 1).reduce((a, b) => a + b, 0) / (j - i + 1)
    for (let k = i; k <= j; k++) out.push({ name: sorted[k].name, position: i + 1, prize: total * share, example: !!sorted[k].example })
    i = j + 1
  }
  return out
}

/** Pays the ranking period to the real player, moves the bar's daily reference, resets the 24 h window and the event. Returns what happened. */
export function newDay(state: State, params: Params): { event: EventClose | null; prizes: RankingPrize[] } {
  const event = state.event.requested.length || state.event.inflow ? closeEvent(state, params) : null
  const prizes = rankingPrizes(state.ranking, state.pots.ranking, params)
  for (const p of prizes) if (!p.example && p.prize > 0) { state.wallet += p.prize; state.pots.ranking -= p.prize }
  state.ranking = state.ranking.filter((r) => r.example)
  for (const l of Object.values(state.levels)) { l.pDayStart = l.p; l.users24h = 0 }
  state.day++
  return { event, prizes }
}

/** What the panel shows per level: registered vs current bar, observed win rate, bonus at the reference median and the equilibrium rate for it. */
export function levelSummary(level: LevelState, params: Params) {
  const lp = params.finite.levels[level.id]
  const bonusAtMedian = lp ? finiteBonusPct(lp.medianRef, level.id, params, level) : 0
  return {
    id: level.id, pIni: level.pIni, p: level.p, users24h: level.users24h, played: level.played, won: level.won,
    observedWinRate: level.played ? (100 * level.won) / level.played : null,
    medianWinScore: level.winScores.length ? median(level.winScores) : null,
    bonusAtMedian, equilibriumWinRate: 100 * equilibriumWinRate(bonusAtMedian, params)
  }
}
